import type {
  LessonData,
  LessonProgress,
  LessonStyle,
  StyleChangeImpact,
} from '@/types/lesson';

export function calculateLessonProgress(data: LessonData): LessonProgress {
  // Lesson progress (35%) - includes text content + other media
  const textProgress = data.textContentViewed
    ? 100
    : Math.min((data.timeSpent / data.estimatedReadTime) * 100, 100);
  const lessonItems = [
    textProgress,
    data.videoProgress,
    data.audioProgress,
    data.mindmapViewed ? 100 : 0,
  ];
  const lessonProgress =
    lessonItems.reduce((sum, item) => sum + item, 0) / lessonItems.length;

  // Notes progress (25%)
  const notesProgress = Math.min(
    ((data.notesTaken + data.personalNotesCount) / 10) * 100,
    100
  );

  // Practice progress (25%)
  const practiceProgress = (data.flashcardsCompleted + data.quizScore) / 2;

  // Reference progress (15%)
  const referenceProgress =
    data.totalSources > 0 ? (data.sourcesViewed / data.totalSources) * 100 : 0;

  // Overall progress calculation
  const overall = Math.round(
    lessonProgress * 0.35 +
      notesProgress * 0.25 +
      practiceProgress * 0.25 +
      referenceProgress * 0.15
  );

  return {
    overall,
    lesson: Math.round(lessonProgress),
    notes: Math.round(notesProgress),
    practice: Math.round(practiceProgress),
    reference: Math.round(referenceProgress),
  };
}

export function getProgressColor(progress: number): string {
  if (progress >= 80) return 'from-green-500 to-green-600';
  if (progress >= 40) return 'from-yellow-500 to-yellow-600';
  return 'from-red-500 to-red-600';
}

export function getProgressBadgeColor(progress: number): string {
  // Light classes unchanged; dark tints tokenized to semantic status tokens.
  if (progress === 100)
    return 'bg-green-100 dark:bg-success/20 text-green-800 dark:text-success';
  if (progress >= 80)
    return 'bg-blue-100 dark:bg-info/20 text-blue-800 dark:text-info';
  if (progress >= 40)
    return 'bg-yellow-100 dark:bg-warning/20 text-yellow-800 dark:text-warning';
  return 'bg-muted dark:bg-gray-700/20 text-foreground';
}

export function handleStyleChange(_newStyle: LessonStyle): StyleChangeImpact {
  return {
    regenerate: {
      learn: true,
      lesson: {
        text: true,
        video: true,
        audio: true,
        mindmap: true,
      },
      notes: {
        ai: true,
        personal: false, // Personal notes are preserved
      },
      practice: {
        flashcards: true,
        quiz: true,
      },
      reference: {
        sources: false, // Original sources preserved
        glossary: true,
        references: false, // External references preserved
      },
    },
    progressAdjustment: {
      resetContent: true,
      preservePersonal: true,
      adjustPractice: true,
    },
  };
}

export function estimateRegenerationTime(impact: StyleChangeImpact): number {
  let timeMinutes = 0;

  if (impact.regenerate.learn) timeMinutes += 1;
  if (impact.regenerate.lesson.text) timeMinutes += 1;
  if (impact.regenerate.lesson.video) timeMinutes += 2;
  if (impact.regenerate.lesson.audio) timeMinutes += 1.5;
  if (impact.regenerate.lesson.mindmap) timeMinutes += 1;
  if (impact.regenerate.notes.ai) timeMinutes += 0.5;
  if (impact.regenerate.practice.flashcards) timeMinutes += 1;
  if (impact.regenerate.practice.quiz) timeMinutes += 1;
  if (impact.regenerate.reference.glossary) timeMinutes += 0.5;

  return Math.ceil(timeMinutes);
}
